import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import NoRecordFound from "../../../components/common/no-record-found";

const ROWS_PER_PAGE = 34;

const ShipmentTrackingRegister = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(delay);
  }, [search]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim(),
      });

      const res = await fetch(
        `${API_BASE_URL}/api/report/shipment-tracking-register?${params.toString()}`,
        {
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Failed to fetch");
      const result = await res.json();

      setData(result.data || []);
      setTotal(result.total || result.data?.length || 0);
    } catch (err) {
      console.error("[🚨 Fetch Error]", err.message || err);
      setError("Failed to load shipment data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [debouncedSearch, page]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-6 bg-white shadow-sm border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Shipment Tracking Register (BL)
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            BL-wise tracking of shipment
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-4 w-4 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                strokeWidth={2}
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by BL No, Supplier, Material..."
            value={search}
            onChange={handleSearchChange}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600">Loading shipment data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <h3 className="text-sm font-medium text-red-800">
            Error loading data
          </h3>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      ) : data.length === 0 ? (
        <NoRecordFound
          message="No shipment records found"
          subMessage="Try changing your search or filters"
          iconClass="text-gray-400"
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-xs">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700 font-semibold">
                <tr>
                  <th className="px-4 py-2 text-left">B/L No.</th>
                  <th className="px-4 py-2 text-left">Purchase Contract</th>
                  <th className="px-4 py-2 text-left">Supplier</th>
                  <th className="px-4 py-2 text-left">Commodity</th>
                  <th className="px-4 py-2 text-left">Vessel</th>
                  <th className="px-4 py-2 text-left">Port of Loading</th>
                  <th className="px-4 py-2 text-left">Port of Discharge</th>
                  <th className="px-4 py-2 text-right">Qty (MT)</th>
                  <th className="px-4 py-2 text-left">Shipment Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {data.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-[#3899cf]">
                      {row.bl_no}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.purchase_contract_no}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.supplier}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.material}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.vessel}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.port_of_loading}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.port_of_discharge}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 text-right font-mono">
                      {row.quantity_shipped}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.shipment_date}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > ROWS_PER_PAGE && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white border-t border-gray-200 rounded-b-xl">
              <p className="text-sm text-gray-700">
                Showing {(page - 1) * ROWS_PER_PAGE + 1} to{" "}
                {Math.min(page * ROWS_PER_PAGE, total)} of {total} records
              </p>

              <div>
                <nav className="relative z-0 inline-flex shadow-sm rounded-md">
                  <button
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className={`px-4 py-2 border text-sm font-medium ${
                      page === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={page === totalPages}
                    className={`px-4 py-2 border text-sm font-medium ${
                      page === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShipmentTrackingRegister;
